import { format } from 'date-fns';
import {
  isCourtHireBookingCalendarVisible,
  isInventoryHireCalendarVisible,
  type CourtHireBookingStatus,
  type InventoryHireStatus,
} from '@court-sync/shared';
import type { MemberCourtHireBookingRow, MemberInventoryHireRow } from '@/services/memberPortal';

export type ConsumerCalendarEquipmentHire = {
  id: string;
  itemName: string;
  quantity: number;
  status: string;
};

export type ConsumerCalendarExtraEvent = {
  id: string;
  kind: 'court_hire';
  date: Date;
  startTime: string;
  endTime: string;
  title: string;
  status: string;
  priceCents?: number;
  currency?: string;
  equipmentHires: ConsumerCalendarEquipmentHire[];
  raw: MemberCourtHireBookingRow;
};

export const COURT_HIRE_EXTRA_EVENT_CHIP_CLASSES =
  'border border-teal-300 dark:border-teal-700 bg-teal-50 dark:bg-teal-900/30 text-teal-900 dark:text-teal-100';

function parseBookingTimes(startAt: string, endAt: string): {
  date: Date;
  startTime: string;
  endTime: string;
} {
  const start = new Date(startAt);
  const end = new Date(endAt);
  const [year, month, day] = format(start, 'yyyy-MM-dd').split('-').map(Number);
  return {
    date: new Date(year, month - 1, day),
    startTime: format(start, 'HH:mm'),
    endTime: format(end, 'HH:mm'),
  };
}

function groupConfirmedEquipmentByCourtBooking(
  inventoryHires: MemberInventoryHireRow[]
): Map<string, ConsumerCalendarEquipmentHire[]> {
  const hiresByBookingId = new Map<string, ConsumerCalendarEquipmentHire[]>();

  for (const hire of inventoryHires) {
    if (!hire.courtHireBookingId) continue;
    if (!isInventoryHireCalendarVisible(hire.status as InventoryHireStatus)) continue;

    const list = hiresByBookingId.get(hire.courtHireBookingId) ?? [];
    list.push({
      id: hire.id,
      itemName: hire.itemName,
      quantity: hire.quantity,
      status: hire.status,
    });
    hiresByBookingId.set(hire.courtHireBookingId, list);
  }

  return hiresByBookingId;
}

export function formatConsumerCalendarEquipmentSummary(
  hires: ConsumerCalendarEquipmentHire[]
): string | null {
  if (hires.length === 0) return null;
  if (hires.length === 1) {
    const hire = hires[0];
    return hire.quantity > 1 ? `${hire.itemName} ×${hire.quantity}` : hire.itemName;
  }
  return `${hires.length} equipment items`;
}

export function mapMemberCourtHireBookingsToExtraEvents(
  bookings: MemberCourtHireBookingRow[],
  inventoryHires: MemberInventoryHireRow[] = []
): ConsumerCalendarExtraEvent[] {
  const equipmentByBookingId = groupConfirmedEquipmentByCourtBooking(inventoryHires);

  return bookings
    .filter((booking) => isCourtHireBookingCalendarVisible(booking.status as CourtHireBookingStatus))
    .map((booking) => {
      const { date, startTime, endTime } = parseBookingTimes(booking.startAt, booking.endAt);
      return {
        id: booking.id,
        kind: 'court_hire' as const,
        date,
        startTime,
        endTime,
        title: booking.courtName ?? 'Court',
        status: booking.status,
        priceCents: booking.priceCents,
        currency: booking.currency,
        equipmentHires: equipmentByBookingId.get(booking.id) ?? [],
        raw: booking,
      };
    });
}

export function extraEventStackKey(event: ConsumerCalendarExtraEvent): string {
  return `court-${event.id}-${format(event.date, 'yyyy-MM-dd')}`;
}

export function countUpcomingExtraEvents(events: ConsumerCalendarExtraEvent[]): number {
  const now = new Date();
  return events.filter((event) => {
    const end = new Date(event.date);
    const [hours, minutes] = event.endTime.split(':').map(Number);
    end.setHours(hours, minutes, 0, 0);
    return (
      end >= now &&
      isCourtHireBookingCalendarVisible(event.status as CourtHireBookingStatus)
    );
  }).length;
}
